/**
 * React Query hooks and helpers for Reports > Fleet Hourly.
 *
 *   useFleetHourly(date, enabled)        GET /v1/retail/fleet-hourly
 *   downloadFleetHourlyXlsx(date)        GET /v1/retail/fleet-hourly/export
 *   useStoreHourly(storeNo, enabled)     GET /v1/stores/omni-hourly?store_no=   (the store drill-in)
 *
 * Every active store hour by hour against the same weekday last year, the fleet
 * counterpart of the Hourly tab on The Sweet Spot's Performance page. Both hooks
 * refetch every five minutes while the page is open and in view, because the sales
 * behind them refresh every fifteen minutes and a stale tab is the usual way an hourly
 * report misleads.
 *
 * Contract:
 *
 *   GET /v1/retail/fleet-hourly
 *     ?date=YYYY-MM-DD   optional; without it each store shows its own local today
 *   -> { scope, business_date, following_local_today, data_through_hour,
 *        oldest_data_through_hour, updated_at, totals, ly_full_day, hours, stores }
 *
 *   GET /v1/stores/omni-hourly?store_no=1114   (today only, the store's own local date)
 *   -> { store_no, business_date, ly_date, timezone, local_hour, data_through_hour,
 *        updated_at, hours, totals, ly_full_day, best_hour }
 *
 * The report types come from `@ampersandbrands/ui`, because the shared screen owns the
 * wire shape. Same 404/503 "not deployed yet" convention as the other report hooks.
 */

import { useQuery } from '@tanstack/react-query';
import type { FleetHourlyHour, FleetHourlyReport, FleetHourlyStore, FleetHourlyTotals } from '@ampersandbrands/ui';
import { apiGet } from './api';
import { RETAIL_API_BASE } from './useRetailStores';
import { STORES_API_BASE } from './useStores';

export const FLEET_HOURLY_API_BASE = `${RETAIL_API_BASE}/fleet-hourly`;

/** How often the page and the drill-in refetch while open: five minutes. */
export const FLEET_HOURLY_REFRESH_MS = 5 * 60 * 1000;

/** `retail_sales_hourly` starts on this day, so an earlier date has nothing to show. */
export const FLEET_HOURLY_EARLIEST_DATE = '2025-02-02';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** The date in the page's URL, or null (each store's own today) for a missing or malformed one. */
export function parseFleetHourlyDate(sp: URLSearchParams): string | null {
  const d = sp.get('date');
  return d !== null && DATE_RE.test(d) ? d : null;
}

/** Write the date into the page's URL, leaving today out. */
export function writeFleetHourlyDate(sp: URLSearchParams, date: string | null): void {
  sp.delete('date');
  if (date !== null) sp.set('date', date);
}

/** Query string for the report and its export. */
export function buildFleetHourlyQs(date: string | null): string {
  const sp = new URLSearchParams();
  if (date !== null) sp.set('date', date);
  return sp.toString();
}

const ZERO_CELL = { ty: null, ly_week: null, comp_week: null };
const ZERO_TOTALS: FleetHourlyTotals = {
  sales: ZERO_CELL,
  transactions: ZERO_CELL,
  traffic: ZERO_CELL,
  conversion: ZERO_CELL,
  dpt: ZERO_CELL,
};

function arr<T>(v: unknown): T[] {
  return Array.isArray(v) ? (v as T[]) : [];
}

function numOrNull(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Tolerant read, so a partial or absent payload degrades to an empty report rather than a crash. */
export function normalizeFleetHourly(raw: unknown): FleetHourlyReport {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    scope: r.scope === 'store' ? 'store' : 'fleet',
    business_date: typeof r.business_date === 'string' ? r.business_date : '',
    following_local_today: r.following_local_today !== false,
    data_through_hour: numOrNull(r.data_through_hour),
    oldest_data_through_hour: numOrNull(r.oldest_data_through_hour),
    updated_at: typeof r.updated_at === 'string' ? r.updated_at : null,
    totals: { ...ZERO_TOTALS, ...((r.totals ?? {}) as Partial<FleetHourlyTotals>) },
    ly_full_day: {
      sales: numOrNull((r.ly_full_day as Record<string, unknown> | undefined)?.sales),
      transactions: numOrNull((r.ly_full_day as Record<string, unknown> | undefined)?.transactions),
    },
    hours: arr<FleetHourlyHour>(r.hours),
    stores: arr<FleetHourlyStore>(r.stores),
  };
}

export function useFleetHourly(date: string | null, enabled: boolean) {
  const query = useQuery<FleetHourlyReport, Error>({
    queryKey: ['retail', 'fleet-hourly', date],
    queryFn: async () => {
      const qs = buildFleetHourlyQs(date);
      return normalizeFleetHourly(await apiGet<unknown>(qs ? `${FLEET_HOURLY_API_BASE}?${qs}` : FLEET_HOURLY_API_BASE));
    },
    // Sales refresh every 15 minutes upstream; a minute of cache is plenty, and the
    // interval keeps an open tab current. Keep the old grid on screen while a new day loads.
    staleTime: 60_000,
    refetchInterval: FLEET_HOURLY_REFRESH_MS,
    refetchIntervalInBackground: false,
    placeholderData: (prev) => prev,
    retry: false,
    enabled,
  });
  return {
    report: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    dataUpdatedAt: query.dataUpdatedAt,
  };
}

/** Browser download of the Excel export, for the day on screen. */
export async function downloadFleetHourlyXlsx(date: string | null): Promise<void> {
  const qs = buildFleetHourlyQs(date);
  const res = await fetch(`${FLEET_HOURLY_API_BASE}/export${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? 'fleet-hourly.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

// ---------------------------------------------------------------------------
// The store drill-in: one store's hours from the Sweet Spot Hourly route
// ---------------------------------------------------------------------------

/** What the drill-in shows for one store: its hours, totals and the day they are for. */
export type StoreHourlyView = {
  store_no: string;
  /** The store's own local date for the hours shown. */
  business_date: string;
  timezone: string | null;
  /** The latest hour the store holds sales for. */
  data_through_hour: number | null;
  /** When the hourly mirror last wrote a row for the day, an ISO timestamp. */
  updated_at: string | null;
  hours: FleetHourlyHour[];
  totals: FleetHourlyTotals;
  ly_full_day: { sales: number | null; transactions: number | null };
};

/** Tolerant read of `GET /v1/stores/omni-hourly`. */
export function normalizeStoreHourly(raw: unknown): StoreHourlyView {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    store_no: typeof r.store_no === 'string' ? r.store_no : '',
    business_date: typeof r.business_date === 'string' ? r.business_date : '',
    timezone: typeof r.timezone === 'string' ? r.timezone : null,
    data_through_hour: numOrNull(r.data_through_hour),
    updated_at: typeof r.updated_at === 'string' ? r.updated_at : null,
    hours: arr<FleetHourlyHour>(r.hours),
    totals: { ...ZERO_TOTALS, ...((r.totals ?? {}) as Partial<FleetHourlyTotals>) },
    ly_full_day: {
      sales: numOrNull((r.ly_full_day as Record<string, unknown> | undefined)?.sales),
      transactions: numOrNull((r.ly_full_day as Record<string, unknown> | undefined)?.transactions),
    },
  };
}

/** The drill-in for a past day reads the store straight out of the fleet response, since omni-hourly serves today only. */
export function storeViewFromFleet(s: FleetHourlyStore, updatedAt: string | null): StoreHourlyView {
  return {
    store_no: s.store_no,
    business_date: s.business_date,
    timezone: s.timezone,
    data_through_hour: s.data_through_hour,
    updated_at: updatedAt,
    hours: s.hours,
    totals: s.totals,
    ly_full_day: s.ly_full_day,
  };
}

export function useStoreHourly(storeNo: string | null, enabled: boolean) {
  const query = useQuery<StoreHourlyView, Error>({
    queryKey: ['stores', 'omni-hourly', storeNo],
    queryFn: async () =>
      normalizeStoreHourly(await apiGet<unknown>(`${STORES_API_BASE}/omni-hourly?store_no=${encodeURIComponent(storeNo ?? '')}`)),
    staleTime: 60_000,
    refetchInterval: FLEET_HOURLY_REFRESH_MS,
    refetchIntervalInBackground: false,
    retry: false,
    enabled: enabled && storeNo !== null,
  });
  return { view: query.data ?? null, isLoading: query.isLoading, error: query.error };
}
